import { useCallback, useEffect, useMemo, useState } from 'react';
import syncIcon from '../../assets/img/patient/indicators/sync.svg';
import { useQueryClient } from '@tanstack/react-query';
import Style from './syncButton.module.css';
import { useSyncInProgress } from '../../features/study/study-selectors';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '../uikit/tooltip/tooltip.component';
import clsx from 'clsx';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { getPatientsQueryKey } from 'src/services/api/api-client/PatientQuery';
import { syncStudyData } from 'src/services/api/api-client/StudyClient';
import { getDoctorStudyByIdQueryKey } from 'src/services/api/api-client/StudyQuery';
import { useRevalidator } from 'react-router-dom';

export const SyncButton = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const study = useStudy();
  const revalidator = useRevalidator();
  const syncingResult = useSyncInProgress(study?.id);
  const [invalidated, setInvalidated] = useState<boolean>(false);
  const [isSyncInProgress, setSyncInProgress] = useState<boolean>(false);

  useEffect(() => {
    if (!syncingResult.result && invalidated) {
      queryClient.invalidateQueries(getPatientsQueryKey()).then(() => {
        revalidator.revalidate();
        setInvalidated(false);
      });

      setSyncInProgress(false);
    }
  }, [invalidated, queryClient, revalidator, syncingResult]);

  const onRefreshSync = useCallback(async () => {
    if (!study?.id) {
      return;
    }

    setSyncInProgress(true);

    syncStudyData(study.id).then(async () => {
      await Promise.all([
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getDoctorStudyByIdQueryKey(study.id)),
      ]);
      revalidator.revalidate();

      setInvalidated(true);
    });
  }, [queryClient, revalidator, study?.id]);

  const memorizedContent = useMemo(() => {
    return (
      <Tooltip text={syncingResult.date} headerText={t('Dashboard.PatientTable.SyncTooltip')}>
        <button
          onClick={onRefreshSync}
          className={Style.syncButton}
          disabled={isSyncInProgress || syncingResult.result}
        >
          <img
            alt={'Sync Button Icon'}
            src={syncIcon}
            className={clsx({ [Style.syncImageProgress]: isSyncInProgress || syncingResult.result })}
          />
        </button>
      </Tooltip>
    );
  }, [syncingResult.date, syncingResult.result, t, onRefreshSync, isSyncInProgress]);

  return <>{memorizedContent}</>;
};
